import React, {useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router";
import SignIn from "./SignIn.jsx";
import LoadingAnimation from "../../../components/LoadingAnimation";
import FormLayout from "../FormLayout";
import { validateUsername } from "../validation";
import { inject, observer } from "mobx-react";
import { getMessageObject, MessageType } from "../FormLayout/MessageFactory";
import { getConfig, SSO_ENABLED } from "../../../lib/configMgr";
import { generateCodeVerifier, generateCodeChallenge, storeVerifier } from '../../../lib/pkce';
import { toast } from "react-toastify";

const Authentication = ({userStore, appStatusStore}) => {

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const {isAuthorized, isAuthenticated, ready: isAuthReady} = userStore;
  const {serverOffline: offline, corsError} = appStatusStore;

  const {
    username: _username = params.get('email') || "",
    password: _password = "",
    message: _message = null,
    from = null,
  } = location.state || {};

  const confirmationStatus = params.get('confirmationStatus');
  const mcpSession = params.get('mcp_session');

  const startGoogleSSO = async () => {
    const { AWSCognito: { authDomain, clientID } } = getConfig();
    const redirectUri = `${window.location.origin}/signin`;
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    storeVerifier(verifier);
    window.location.href = [
      `https://${authDomain}/oauth2/authorize`,
      `?identity_provider=Google`,
      `&client_id=${clientID}`,
      `&response_type=code`,
      `&scope=openid+email+profile`,
      `&redirect_uri=${encodeURIComponent(redirectUri)}`,
      `&code_challenge=${encodeURIComponent(challenge)}`,
      `&code_challenge_method=S256`,
    ].join('');
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) {
      setSsoInProgress(true);
      window.history.replaceState(null, '', window.location.pathname);
      const redirectUri = `${window.location.origin}/signin`;
      userStore.loginWithCode(code, redirectUri)
        .catch(error => {
          setSsoInProgress(false);
          setMessageObject(getMessageObject(error, ""));
        });
      return;
    }

    const hash = window.location.hash;
    if (!hash) return;
    const hashParams = new URLSearchParams(hash.substring(1));
    const errorDesc = hashParams.get('error_description') || '';

    if (errorDesc.includes('linked')) {
      setSsoInProgress(true);
      window.history.replaceState(null, '', window.location.pathname);
      startGoogleSSO().catch(error => {
        setSsoInProgress(false);
        setMessageObject(getMessageObject(error, ""));
      });
      return;
    }

    if (errorDesc) {
      setSsoInProgress(false);
      window.history.replaceState(null, '', window.location.pathname);
      setMessageObject({message: errorDesc.replace(/\.\s*$/, ''), type: "error"});
      return;
    }
  }, []);

  const [username, setUsername] = useState(_username);
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState(_password);

  // Convert info messages to toast, keep error/warning messages in form
  const initialMessage = confirmationStatus === "0"
    ? getMessageObject(MessageType.UserConfirmed, username)
    : _message;

  const [messageObject, setMessageObject] = useState(() => {
    if (initialMessage && initialMessage.type === "info") {
      toast.info(initialMessage.message);
      return null;
    }
    return initialMessage;
  });
  const [isRequestProcessing, setIsRequestProcessing] = useState(false);
  const [ssoInProgress, setSsoInProgress] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('code')) return true;
    const hash = window.location.hash;
    if (!hash) return false;
    const hashParams = new URLSearchParams(hash.substring(1));
    return !!(hashParams.get('error_description'));
  });

  useEffect(() => {
    if (_message === null && isAuthenticated && isAuthorized === false) {
      setMessageObject(getMessageObject(MessageType.NotAuthorized));
    }
  }, [_message, isAuthenticated, isAuthorized]);

  useEffect(() => {
    if (_message != null) {
      // Convert info messages to toast, keep error/warning messages in form
      if (_message.type === "info") {
        toast.info(_message.message);
        setMessageObject(null);
      } else {
        setMessageObject(_message);
      }
    }
  }, [_message])

  useEffect(() => {
    if (corsError) {
      setMessageObject(getMessageObject(MessageType.CORSError));
    }
    else if (offline) {
      navigate("/maintenance", {replace:true, state: location.state});
    }
    else if (isAuthorized) {
      if (mcpSession) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/oauth/complete';
        const addField = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };
        addField('mcp_session', mcpSession);
        addField('access_token', userStore.jwtToken);
        document.body.appendChild(form);
        form.submit();
        return;
      }
      const ssoFrom = sessionStorage.getItem('sso-redirect-from');
      if (ssoFrom) {
        sessionStorage.removeItem('sso-redirect-from');
        navigate(ssoFrom, {replace: true});
      } else if (from != null) {
        navigate(from, {replace: true});
      }
      else {
        navigate("/", {replace: true});
      }
    }
  }, [isAuthorized, offline, corsError]);

  const handleSignin = () => {
    setIsRequestProcessing(true);
    setMessageObject(null);
    const {from = "/"} = location.state || {};
    userStore
      .login(username, password, navigate)
      .then(() => {
        if (!mcpSession) {
          navigate(from, {replace:true});
        }
      })
      .catch(error => {
        setIsRequestProcessing(false);
        if (error.name === MessageType.UserLambdaValidationException) {
          handleLambdaValidatonException(error);
        }
        else if (error.name === MessageType.UserNotConfirmedException) {
          navigate("/verify", {
            replace: true,
            state: {
              username,
              password,
              message: getMessageObject(MessageType.UserNotConfirmedException, username),
            }
          });
        }
        else if (error.name === MessageType.LimitExceededException) {
          setMessageObject(getMessageObject(error, username));
        }
        else if (error.name === MessageType.NotAuthorized) {
          setMessageObject(getMessageObject(MessageType.NotAuthorized, error.message));
        }
        else {
          setMessageObject(getMessageObject(error, username));
        }
      });
  };

  useEffect(() => {
    if (_username !== "" && _password !== "") {
      handleSignin();
    }
  }, [_username, _password]);

  const handleLambdaValidatonException = error => {
    const msg = error && error.message ? error.message : "";
    if (msg.includes("password-expired")) {
      navigate("/changepass", {
        replace: true,
        state: {
          username: username,
          oldPassword: password,
          verificationCode: msg.replace(/.*password-expired:([0-9a-f]+).*/, "$1"),
          message: getMessageObject(MessageType.PasswordExpired, username)
        }
      });
    }
    else if (msg.includes("service-unavailable")) {
      setMessageObject(getMessageObject(MessageType.ServiceUnavailable, username));
    }
    else if (msg.includes("unauthorized")) {
      setMessageObject(getMessageObject(MessageType.NotAuthorizedException, username));
    }
    else {
      // Show generic login error message; full details are sent to Sentry
      setMessageObject(getMessageObject(MessageType.LoginError, username));
    }
  };

  const redirectTo = (path, state = {}) => {
    navigate(path, {replace: true, state: { ...state, username, password }});
  };

  const handleInputChange = (fn, value) => {
    if (fn === setUsername) {
      value = value.trim();
    }
    fn(value);
    setMessageObject(null);
  };

  const usernameValid = validateUsername(username) === false;
  useEffect(() => {
    if (username && !usernameValid && usernameTouched) {
      setMessageObject(getMessageObject(MessageType.InvalidUsername, username));
    }
  }, [username, usernameValid, usernameTouched]);

  const isCheckingSession = !isAuthReady;

  if (ssoInProgress) {
    return (
      <div className="authorizing-placeholder">
        <LoadingAnimation />
      </div>
    );
  }

  const showSpinner = mcpSession && (isCheckingSession || isAuthorized !== false);

  const enableSubmit =
    username && password && usernameValid && !isRequestProcessing && !isAuthorized && !isCheckingSession;

  return (
    <FormLayout message={showSpinner ? null : messageObject}>
      {showSpinner ? <LoadingAnimation style={{ margin: '1rem auto' }} /> : <SignIn
        username={username}
        enableSubmit={enableSubmit}
        isCheckingSession={isCheckingSession}
        isRequestProcessing={isRequestProcessing}
        ssoEnabled={SSO_ENABLED}
        ssoInProgress={ssoInProgress}
        onUsernameChange={e => handleInputChange(setUsername, e.target.value)}
        onUsernameBlur={e => setUsernameTouched(true)}
        password={password}
        onPasswordChange={e => handleInputChange(setPassword, e.target.value)}
        onSignIn={handleSignin}
        onGoogleSignIn={() => {
          setSsoInProgress(true);
          if (from) {
            sessionStorage.setItem('sso-redirect-from', typeof from === 'string' ? from : `${from.pathname}${from.search}${from.hash}`);
          }
          startGoogleSSO().catch(error => {
            setSsoInProgress(false);
            setMessageObject(getMessageObject(error, ""));
          });
        }}
        onResendVerification={_ => {
          userStore.resendConfirmation(username)
            .then(() => {
              const email = username || "your email";
              toast.info(`A new confirmation code has been sent to ${email}. Enter it here to complete registration.`);
              redirectTo("/verify", {
                message: null
              })
            })
            .catch(error => {
              if (error?.name === "UserNotFoundException") {
                redirectTo("/register", {
                  message: {message: "Please register", type: "warning"},
                });
              }
              else {
                setMessageObject({message: `Unexpected error: ${error.name}`, type: "error"});
              }
            });
        }}
        onRegisterClick={_ => redirectTo("/register")}
        onForgotPass={_ => redirectTo("/resetpass", {username})}
      />}
    </FormLayout>
  );
}

export default inject("appStatusStore", "userStore")(observer(Authentication));
